<template>
  <div class="item-list fade-in">
    <!-- === GLOBAL LOADER === -->
    <transition name="fade-slide">
      <div v-if="loading" class="global-loader">
        <div class="loader-spinner"></div>
        <p>Loading...</p>
      </div>
    </transition>

    <!-- === KPI CARDS === -->
    <div class="kpi-row fade-in">
      <div class="kpi-card" v-for="(kpi, i) in itemStats" :key="i">
        <div>
          <h4>{{ kpi.title }}</h4>
          <div class="value">{{ kpi.value }}</div>
        </div>
        <i :class="kpi.icon + ' kpi-icon'"></i>
      </div>
    </div>

    <!-- === FILTERS + ACTION BUTTONS === -->
    <div class="grid-header fade-in">
      <div class="filters">
        <div class="search-box">
          <i class="bx bx-search"></i>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search items..."
            @input="loadItems"
          />
        </div>

       <div class="filter-actions">
        <button class="btn-outline" @click="triggerImport">
          <i class="bx bx-import"></i> Import
        </button>

        <button class="btn-outline" @click="doExport">
          <i class="bx bx-export"></i> Export
        </button>

        <button class="btn-outline" @click="downloadSample">
          <i class="bx bx-download"></i> Sample
        </button>
      </div>

      </div>

      <button class="btn-primary" @click="addItem">
        <i class="bx bx-plus"></i> Add Item
      </button>
    </div>

    <!-- === ITEM CARDS GRID === -->
    <div v-if="filteredItems.length" class="website-grid fade-in">
      <div class="website-card" v-for="(item, i) in filteredItems" :key="i">
        <!-- Header -->
        <div class="website-card-header">
          <h4><i class="bx bx-package"></i> {{ item.name }}</h4>
        </div>

        <!-- Body -->
        <div class="website-card-body">
          <div class="item-info">
            <div class="info-item">
              <i class="bx bx-category"></i>
              <span><strong>Type:</strong> {{ item.type || "—" }}</span>
            </div>
            <div class="info-item">
              <i class="bx bx-layer"></i>
              <span><strong>Stock Qty:</strong> {{ item.stock || 0 }}</span>
            </div>
            <div class="info-item">
              <i class="bx bx-dollar-circle"></i>
              <span><strong>Price/Unit:</strong> ${{ item.price }}</span>
            </div>
            <div class="info-item">
              <i class="bx bx-pulse"></i>
              <span><strong>Status:</strong>
                <span
                  :class="['status-pill', item.status === 'active' ? 'active' : 'inactive']"
                >
                  {{ item.status }}
                </span>
              </span>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="website-card-footer">
          <span class="website-domain">Added on: {{ item.createdAt }}</span>
          <div class="footer-actions">
            <button class="btn-edit" @click="editItem(item)">
              <i class="bx bx-edit"></i> Edit
            </button>
            <button class="btn-delete" @click="handleDelete(item)">
              <i class="bx bx-trash"></i> Delete
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- === EMPTY STATE === -->
    <div v-else class="empty-state fade-in">
      <i class="bx bx-box" style="font-size: 2rem; color: var(--primary);"></i>
      <p>No items found. Add a new item to get started!</p>
      <button class="btn-primary mt-2" @click="addItem"
          style="padding: 8px 14px; font-size: 13px; border-radius: 6px;">
        <i class="bx bx-plus" style="font-size: 14px; color: white; margin-top: .7rem;"></i> Add New Item
      </button>
    </div>
    


    <!-- === PAGINATION === -->
    <div v-if="filteredItems.length" class="pagination fade-in">
      <span>Showing {{ filteredItems.length }} of {{ items.length }} items</span>
      <div class="pagination-buttons">
        <button><i class="bx bx-chevron-left"></i></button>
        <button class="active">1</button>
        <button>2</button>
        <button><i class="bx bx-chevron-right"></i></button>
      </div>
    </div>

    <br /><br />
     <ConfirmDialog
  :show="showConfirm"
  :title="confirmTitle"
  :message="confirmMessage"
  @cancel="showConfirm = false"
  @confirmAction="handleConfirm"
/>
<input
  type="file"
  id="itemImportFile"
  accept=".csv"
  style="display:none"
  @change="handleImportFile"
/>
  </div>
</template>


<script setup scoped>
import { ref, computed, onMounted } from "vue";
import { useToast } from "vue-toastification";
import { getItems, deleteItem, importItems, exportItems, downloadItemSample } from "@/apis/itemapis";
import ConfirmDialog from "@/comps/ConfirmDialog.vue";


const toast = useToast();
const loading = ref(false);
const searchQuery = ref("");
const items = ref([]);
const page_no = ref(0);

const itemStats = ref([
  { title: "Total Items", value: 0, icon: "bx bx-box" },
  { title: "Active", value: 0, icon: "bx bx-package" },
  { title: "Inactive", value: 0, icon: "bx bx-block" },
  { title: "Recently Added", value: 0, icon: "bx bx-time-five" },
]);

const showConfirm = ref(false);
const confirmTitle = ref("");
const confirmMessage = ref("");
let confirmCallback = () => {};

const openConfirm = (title, message, onConfirm) => {
  confirmTitle.value = title;
  confirmMessage.value = message;
  confirmCallback = onConfirm;
  showConfirm.value = true;
};

const handleConfirm = () => {
  showConfirm.value = false;
  if (typeof confirmCallback === "function") confirmCallback();
};

// ✅ Fetch items from backend
async function loadItems() {
  try {
    loading.value = true;
    const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
    const res = await getItems(page_no.value, userData.user_id || "", searchQuery.value);

    if (res?.status && res.records) {
      items.value = res.records.map((it) => ({
        id: it.item_id,
        name: it.item_name,
        type: it.item_type,
        stock: it.item_stock_qty,
        price: it.item_unit_price,
        status: it.item_status,
        createdAt: new Date(it.item_created_at).toLocaleDateString(),
      }));

      // 📊 KPI update
      itemStats.value[0].value = res.total1 || items.value.length;
      itemStats.value[1].value = items.value.filter((i) => i.status === "active").length;
      itemStats.value[2].value = items.value.filter((i) => i.status === "inactive").length;
      itemStats.value[3].value = items.value.slice(0, 5).length;
    } else {
      toast.error("Failed to load items");
    }
  } catch (err) {
    console.error("loadItems error:", err);
    toast.error("Error loading items");
  } finally {
    loading.value = false;
  }
}

// 🔹 Trigger file select
function triggerImport() {
  document.getElementById("itemImportFile").click();
}

// 🔹 Handle file input & call API
async function handleImportFile(e) {
  const file = e.target.files[0];
  if (!file) return;

  try {
    loading.value = true;
    toast.info("Importing items...");

    const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};

    const res = await importItems(file, userData.user_id);

    if (res.status) {
      toast.success(res.success || "Items imported successfully!");
      await loadItems();
    } else {
      toast.error(res.success || "Failed to import items");
    }
  } catch (err) {
    console.error("handleImportFile error:", err);
    toast.error("Import failed");
  } finally {
    loading.value = false;
    e.target.value = ""; // reset input
  }
}

// 🔹 Export items
async function doExport() {
  try {
    const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};

    toast.info("Exporting items...");
    const res = await exportItems(userData.user_id);

    if (res.status) {
      toast.success("Items exported successfully!");
    } else {
      toast.error("Failed to export items");
    }
  } catch (err) {
    console.error("export error:", err);
    toast.error("Error exporting items");
  }
}

// 🔹 Download Sample CSV
async function downloadSample() {
  try {
    toast.info("Preparing sample file...");
    const res = await downloadItemSample();

    if (res.status) {
      toast.success("Sample file downloaded!");
    } else {
      toast.error("Failed to download sample");
    }
  } catch (err) {
    console.error("download sample error:", err);
    toast.error("Error downloading sample file");
  }
}


// ✅ Delete item (with confirm dialog)
async function handleDelete(item) {
  openConfirm(
    "Delete Item",
    `Are you sure you want to delete?`,
    async () => {
      try {
        loading.value = true;
        const res = await deleteItem(item.id);
        if (res.status) {
          toast.success("Item deleted successfully!");
          await loadItems();
        } else {
          toast.error("Failed to delete item");
        }
      } catch (err) {
        console.error("deleteItem error:", err);
        toast.error("Error deleting item");
      } finally {
        loading.value = false;
      }
    }
  );
}

// ✅ Filtered items
const filteredItems = computed(() => {
  return items.value.filter((i) =>
    i.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

// ✅ Add/Edit navigation
function addItem() {
  window.location.href = "/new_item";
}
function editItem(item) {
  window.location.href = `/new_item?id=${item.id}`;
}

onMounted(() => {
  loadItems();
});
</script>


<style scoped>
/* === Animation === */
.fade-in {
  animation: fadeInUp 0.6s ease forwards;
  opacity: 0;
  transform: translateY(12px);
}
@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* === Filters === */
.filters {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.filter-actions {
  display: flex;
  gap: 10px;
}
.btn-outline {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
}
.btn-outline:hover {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}

/* === Item Info === */
.item-info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  font-size: 13px;
  color: var(--text);
}
.info-item {
  display: flex;
  align-items: center;
  gap: 6px;
}
.info-item i {
  color: var(--primary);
  font-size: 1rem;
}

/* === Footer Actions === */
.footer-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.btn-edit {
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-edit:hover {
  background: #1e3a8a;
  transform: translateY(-1px);
}
.btn-delete {
  background: #f3f4f6;
  color: var(--text);
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-delete:hover {
  background: #fee2e2;
  color: #991b1b;
  transform: translateY(-1px);
}

/* === Pagination === */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
}
.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
}
.pagination-buttons button {
  border: none;
  background: #f3f4f6;
  color: var(--text);
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.pagination-buttons button.active {
  background: var(--primary);
  color: #fff;
}
.pagination-buttons button:hover {
  background: var(--primary);
  color: #fff;
}

/* === Responsive === */
@media (max-width: 768px) {
  .item-info {
    grid-template-columns: 1fr;
  }
  .filter-actions {
    width: 100%;
    justify-content: space-between;
  }
}

/* === 📱 PATCH: Enhanced Mobile Responsiveness for Item List === */
@media (max-width: 992px) {
  /* Compact grid and balanced layout for tablets */
  .website-grid {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 1.2rem !important;
  }

  .pagination {
    justify-content: center !important;
    gap: 1rem !important;
  }

  .pagination span {
    width: 100% !important;
    text-align: center !important;
    font-size: 0.9rem !important;
    color: var(--muted);
  }

  .pagination-buttons {
    justify-content: center !important;
    gap: 0.5rem !important;
  }

  .pagination-buttons button {
    min-width: 36px !important;
    height: 36px !important;
    font-size: 0.9rem !important;
  }
}

@media (max-width: 768px) {
  /* Stack filter bar */
  .grid-header {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 1rem !important;
  }

  .filters {
    flex-direction: column !important;
    align-items: stretch !important;
    width: 100% !important;
    gap: 0.8rem !important;
  }

  .search-box {
    width: 100% !important;
    border-radius: 8px !important;
  }

  .filter-actions {
    flex-wrap: wrap !important;
    justify-content: space-between !important;
    width: 100% !important;
  }

  .filter-actions button {
    flex: 1 1 30% !important;
    text-align: center !important;
    font-size: 0.85rem !important;
  }

  /* Cards become one per row */
  .website-grid {
    grid-template-columns: 1fr !important;
    gap: 1rem !important;
  }

  .website-card {
    padding: 1.2rem !important;
  }

  .item-info {
    grid-template-columns: 1fr !important;
    gap: 0.5rem !important;
  }

  /* Footer actions stack vertically for clarity */
  .footer-actions {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 0.5rem !important;
  }

  .footer-actions button {
    width: 100% !important;
  }

  /* Pagination fully centered and clean */
  .pagination {
    flex-direction: column !important;
    align-items: center !important;
    gap: 0.8rem !important;
  }

  .pagination-buttons {
    justify-content: center !important;
    flex-wrap: wrap !important;
    width: 100% !important;
  }

  .pagination-buttons button {
    flex: 1 1 20% !important;
    font-size: 0.85rem !important;
    padding: 0.5rem !important;
  }
}

@media (max-width: 480px) {
  /* Mobile Compact UI */
  .btn-outline,
  .btn-primary {
    width: 100% !important;
    justify-content: center !important;
  }

  .pagination-buttons button {
    flex: 1 1 28% !important;
  }

  .pagination span {
    font-size: 0.85rem !important;
  }

  .kpi-row {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 0.8rem !important;
  }

  .website-card h4 {
    font-size: 1rem !important;
  }

  .info-item {
    font-size: 0.85rem !important;
  }
}
/* === 🌟 ADDITIONAL PATCH: Enhanced Item List UI === */

/* ✅ Loader */
.global-loader {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(3px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 3000;
}
.loader-spinner {
  width: 42px;
  height: 42px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid var(--primary, #111827);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 0.8rem;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.global-loader p {
  font-weight: 500;
  color: #111827;
}

/* ✅ KPI Row Cards */
.kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}
.kpi-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem 1.2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(17, 24, 39, 0.05);
}
.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 14px rgba(17, 24, 39, 0.08);
}
.kpi-card h4 {
  font-size: 0.9rem;
  color: #6b7280;
  font-weight: 500;
}
.kpi-card .value {
  font-size: 1.4rem;
  font-weight: 700;
  color: #111827;
}
.kpi-icon {
  font-size: 1.8rem;
  color: var(--primary, #111827);
  opacity: 0.8;
}

/* ✅ Status Pill (Active/Inactive) */
.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: capitalize;
  transition: all 0.25s ease;
}
.status-pill.active {
  background: #dcfce7;
  color: #166534;
}
.status-pill.inactive {
  background: #fee2e2;
  color: #991b1b;
}

/* ✅ Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  border: 2px dashed #e5e7eb;
  border-radius: 12px;
  background: #f9fafb;
  color: #6b7280;
  text-align: center;
}
.empty-state i {
  margin-bottom: 0.5rem;
}
.empty-state p {
  font-size: 1rem;
  margin-bottom: 1rem;
}

/* ✅ Buttons: smoother hover */
.btn-primary {
  background: var(--primary, #111827);
  color: #fff;
  border-radius: 8px;
  border: none;
  padding: 0.6rem 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-primary:hover {
  background: #1f2937;
  transform: translateY(-1px);
}

.btn-outline {
  border: 1px solid var(--primary, #111827);
  color: var(--primary, #111827);
  font-weight: 600;
  border-radius: 8px;
  background: #fff;
  padding: 0.6rem 1rem;
  transition: all 0.3s ease;
}
.btn-outline:hover {
  background: var(--primary, #111827);
  color: #fff;
}

/* ✅ Hover Lift for Cards */
.website-card {
  transition: all 0.3s ease;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
}
.website-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 14px rgba(17, 24, 39, 0.08);
}

/* ✅ Responsive Fixes */
@media (max-width: 768px) {
  .kpi-row {
    grid-template-columns: repeat(2, 1fr);
  }
  .empty-state {
    padding: 2rem 1rem;
  }
}
@media (max-width: 480px) {
  .kpi-row {
    grid-template-columns: 1fr;
  }
  .status-pill {
    font-size: 0.75rem;
  }
}

</style>
